/**
 * Currency Input Component
 * 
 * Input de valor monetário com comportamento de cursor à direita
 * e preenchimento direita-esquerda, seguindo o padrão de bancos e Excel.
 * 
 * Solicitado pelo cliente no PDF: CorreçõesLibrattum05122025.pdf
 * 
 * Melhorias aplicadas:
 * - Cursor sempre posicionado à direita (inclusive ao clicar no campo)
 * - Máximo de 2 casas decimais garantido
 * - Preenchimento direita-esquerda (estilo calculadora)
 */
import * as React from "react";
import { cn } from "@/lib/utils";

interface CurrencyInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: number | string;
  onChange: (value: number) => void;
  prefix?: string;
  decimalPlaces?: number;
}

const CurrencyInput = React.forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      className,
      value,
      onChange,
      prefix = "R$",
      decimalPlaces = 2,
      disabled,
      onDoubleClick: onDoubleClickProp,
      ...props
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const isSelectingAllRef = React.useRef(false);
    const isAllTextSelected = React.useCallback(() => {
      const input = inputRef.current;
      if (!input) return false;
      return input.selectionStart === 0 && input.selectionEnd === input.value.length;
    }, []);
    const combinedRef = (node: HTMLInputElement) => {
      inputRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    // Converte o valor numérico para string formatada para exibição
    const formatForDisplay = React.useCallback((val: number | string): string => {
      const numericValue = typeof val === 'string' ? Number.parseFloat(val) || 0 : val;
      // Formata para BRL sem o símbolo (adicionamos manualmente)
      return numericValue.toLocaleString('pt-BR', {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });
    }, [decimalPlaces]);

    // Estado interno para o valor exibido
    const [displayValue, setDisplayValue] = React.useState(() => formatForDisplay(value));

    // Atualiza quando o valor externo muda
    React.useEffect(() => {
      setDisplayValue(formatForDisplay(value));
    }, [value, formatForDisplay]);

    // Função utilitária para mover o cursor para o final
    const moveCursorToEnd = React.useCallback(() => {
      if (inputRef.current && !disabled) {
        const len = inputRef.current.value.length;
        inputRef.current.setSelectionRange(len, len);
      }
    }, [disabled]);

    // Manipula a entrada do usuário - preenchimento direita-esquerda
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      
      // Remove tudo que não for dígito
      const digits = input.replace(/\D/g, '');
      
      if (digits === '') {
        setDisplayValue('0,00');
        onChange(0);
        return;
      }

      // Converte para centavos e depois para reais
      const centavos = Number.parseInt(digits, 10);
      const reais = centavos / Math.pow(10, decimalPlaces);
      
      // Atualiza o display formatado
      setDisplayValue(formatForDisplay(reais));
      
      // Notifica a mudança
      onChange(reais);

      // Garante que o cursor fique no final após a mudança
      setTimeout(moveCursorToEnd, 0);
    };

    // Ao focar, move o cursor para o final
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setTimeout(() => {
        moveCursorToEnd();
      }, 0);
    };

    // Ao clicar (mesmo se já focado), move o cursor para o final
    const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
      if (e.detail >= 2) return;
      // Previne a seleção padrão do navegador
      e.preventDefault();
      moveCursorToEnd();
    };

    // Ao mover o cursor com teclas de seta ou Home/End, força para o final
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Permite Backspace, Delete, Tab, Enter
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape'];
      
      // Bloqueia teclas de navegação que moveriam o cursor
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(e.key)) {
        e.preventDefault();
        moveCursorToEnd();
        return;
      }

      // Permite números
      if (/^\d$/.test(e.key) || allowedKeys.includes(e.key)) {
        return;
      }

      // Bloqueia outras teclas (letras, símbolos especiais)
      if (!e.ctrlKey && !e.metaKey) {
        e.preventDefault();
      }
    };

    // Ao dar duplo clique, seleciona todo o conteúdo
    const handleDoubleClick = (e: React.MouseEvent<HTMLInputElement>) => {
      isSelectingAllRef.current = true;
      (e.target as HTMLInputElement).select();
      onDoubleClickProp?.(e);
    };

    // Impede seleção com mouse (arraste)
    const handleSelect = () => {
      if (isAllTextSelected()) return;
      if (isSelectingAllRef.current) {
        // Mantém a seleção do duplo clique ativa.
        setTimeout(() => {
          isSelectingAllRef.current = false;
        }, 0);
        return;
      }
      // Após qualquer seleção, move o cursor para o final
      setTimeout(() => {
        if (isAllTextSelected()) return;
        moveCursorToEnd();
      }, 10);
    };

    return (
      <div className={cn(
        "flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 h-10",
        "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
        disabled && "cursor-not-allowed opacity-50",
        className
      )}>
        <span className="text-muted-foreground text-sm font-medium">{prefix}</span>
        <input
          ref={combinedRef}
          type="text"
          inputMode="decimal"
          className={cn(
            "flex-1 bg-transparent text-right outline-none",
            "text-base font-medium placeholder:text-muted-foreground",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "select-text"
          )}
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onDoubleClick={handleDoubleClick}
          onSelect={handleSelect}
          disabled={disabled}
          autoComplete="off"
          {...props}
        />
      </div>
    );
  }
);

CurrencyInput.displayName = "CurrencyInput";

export { CurrencyInput };






